import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/config';
import i18n from '../i18n';

const OFFLINE_CACHE_KEY = 'trips_offline_cache';

/**
 * Recupera i metadati di tutti i trip accessibili tramite token.
 * In preview mode (?from=portfolio) restituisce dati completi senza auth.
 * Se offline, serve i dati dall'ultima cache localStorage.
 */
export function useTrips(token, setIsStartupDialogOpen, setSessionToken, previewMode = null) {
    const [trips, setTrips]         = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [isPreview, setIsPreview] = useState(false);
    const [isOffline, setIsOffline] = useState(false);

    useEffect(() => {
        const onUnauthorized = () => {
            setSessionToken('');
            setIsStartupDialogOpen(true);
        };

        async function fetchTrips() {
            // ── Preview mode ──────────────────────────────────────────────────
            if (previewMode === 'portfolio') {
                try {
                    setLoading(true);
                    setError(null);
                    setIsPreview(true);
                    const res = await fetch(`${API_BASE_URL}/api/trips?from=portfolio`);
                    if (!res.ok) throw new Error(i18n.t('errors.previewLoad'));
                    const data = await res.json();
                    setTrips(data.trips || []);
                    setIsStartupDialogOpen(false);
                } catch (err) {
                    setError(err.message);
                    setTrips([]);
                } finally {
                    setLoading(false);
                }
                return;
            }

            // ── Nessun token ─────────────────────────────────────────────────
            if (!token) {
                setTrips([]);
                setLoading(false);
                setError(null);
                setIsStartupDialogOpen(true);
                return;
            }

            // ── Fetch autenticato ─────────────────────────────────────────────
            try {
                setLoading(true);
                setError(null);
                setIsPreview(false);
                setIsOffline(false);

                const res = await fetch(`${API_BASE_URL}/api/trips`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) {
                    if (res.status === 401) { onUnauthorized(); return; }
                    throw new Error(i18n.t('errors.tripsLoad'));
                }

                const data = await res.json();
                const trips = data.trips || [];
                setTrips(trips);
                setIsStartupDialogOpen(false);

                // Salva in cache per uso offline
                try { localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(trips)); } catch {}

            } catch (err) {
                // Fallback offline: usa cache localStorage
                try {
                    const cached = localStorage.getItem(OFFLINE_CACHE_KEY);
                    if (cached) {
                        setTrips(JSON.parse(cached));
                        setIsOffline(true);
                        setIsStartupDialogOpen(false);
                        return;
                    }
                } catch {}

                setError(err.message);
                setTrips([]);
            } finally {
                setLoading(false);
            }
        }

        fetchTrips();
    }, [token, previewMode, setIsStartupDialogOpen, setSessionToken]);

    return { trips, loading, error, isPreview, isOffline };
}

/**
 * Recupera i dati completi di un singolo trip (lazy load).
 * tripId === null → restituisce subito senza fetch (usato in preview mode).
 */
export function useTrip(tripId, token) {
    const [trip, setTrip]       = useState(null);
    const [loading, setLoading] = useState(!!tripId);
    const [error, setError]     = useState(null);

    useEffect(() => {
        if (!tripId) {
            setLoading(false);
            return;
        }

        async function fetchTrip() {
            try {
                setLoading(true);
                setError(null);

                const res = await fetch(`${API_BASE_URL}/api/trip/${tripId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) {
                    if (res.status === 401) throw new Error(i18n.t('errors.sessionExpired'));
                    if (res.status === 403) throw new Error(i18n.t('errors.noAccess'));
                    if (res.status === 404) throw new Error(i18n.t('errors.notFound'));
                    throw new Error(i18n.t('errors.tripLoad'));
                }

                setTrip(await res.json());
            } catch (err) {
                setError(err.message);
                setTrip(null);
            } finally {
                setLoading(false);
            }
        }

        fetchTrip();
    }, [tripId, token]);

    return { trip, loading, error };
}
